// API service for making HTTP requests to the backend

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  timeoutMs?: number;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  total?: number;
}

import { resolveApiBaseUrl, isRemoteEndpoint, getRemoteBackendUrl } from '../config/backendRouting';
import BACKEND_CONFIG from '../config/backendConfig';

const GATEWAY_TIMEOUT_MSG = 'Request timed out or server busy. Try again or use fewer filters.';

export const makeRequest = async <T = any>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  params?: Record<string, any>,
  body?: any,
  options?: { timeoutMs?: number }
): Promise<T> => {
  const timeoutMs = options?.timeoutMs ?? 0;
  let controller: AbortController | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  if (timeoutMs > 0) {
    controller = new AbortController();
    timeoutId = setTimeout(() => controller!.abort(), timeoutMs);
  }
  try {
    const baseUrl = resolveApiBaseUrl(endpoint).replace(/\/+$/, '');
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const pathForUrl = baseUrl ? `${baseUrl}${path}` : path;
    const url =
      typeof window !== 'undefined'
        ? new URL(pathForUrl, window.location.origin)
        : new URL(
            pathForUrl.startsWith('http') ? pathForUrl : `${BACKEND_CONFIG.PRIMARY_URL.replace(/\/+$/, '')}${path}`
          );
    
    // Add query parameters for GET requests
    if (method === 'GET' && params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const requestOptions: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      signal: controller?.signal,
    };

    // Add body for non-GET requests
    if (method !== 'GET' && body) {
      requestOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url.toString(), requestOptions);
    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status >= 502 && response.status <= 504) {
        if (isRemoteEndpoint(endpoint)) {
          throw new Error(`Remote flow service temporarily unavailable (${getRemoteBackendUrl()}).`);
        }
        throw new Error(GATEWAY_TIMEOUT_MSG);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        const timeoutErr = new Error(GATEWAY_TIMEOUT_MSG);
        console.warn(`API request timed out for ${endpoint}`);
        throw timeoutErr;
      }
      if (error.message === GATEWAY_TIMEOUT_MSG) {
        console.warn(`API request failed for ${endpoint}:`, error.message);
        throw error;
      }
      if (isRemoteEndpoint(endpoint) && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
        throw new Error(`Remote flow service temporarily unavailable (${getRemoteBackendUrl()}).`);
      }
    }
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};

export const get = async <T = any>(endpoint: string, params?: Record<string, any>): Promise<T> => {
  return makeRequest<T>(endpoint, 'GET', params);
};

export const post = async <T = any>(endpoint: string, body?: any, params?: Record<string, any>): Promise<T> => {
  return makeRequest<T>(endpoint, 'POST', params, body);
};

export const put = async <T = any>(endpoint: string, body?: any, params?: Record<string, any>): Promise<T> => {
  return makeRequest<T>(endpoint, 'PUT', params, body);
};

export const del = async <T = any>(endpoint: string, params?: Record<string, any>): Promise<T> => {
  return makeRequest<T>(endpoint, 'DELETE', params);
};

// Helper function to handle API errors
export const handleApiError = (error: any): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unknown error occurred';
};

// Helper function to check if response is successful
export const isSuccessfulResponse = (response: ApiResponse): boolean => {
  return response.success === true;
};

export default {
  makeRequest,
  get,
  post,
  put,
  del,
  handleApiError,
  isSuccessfulResponse
};
